import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { api } from '../contexts/ApiContext';

export type TxPreviewDocument = {
  open_url?: string;
  file_name?: string;
  document_type?: string;
} | null;

const TITLE_BY_TYPE: Record<string, string> = {
  receipt: 'Receipt',
  sales_invoice: 'Sales invoice',
  purchase_invoice: 'Purchase invoice',
};

function guessPreviewMime(mimeType?: string | null, fileName?: string | null): string {
  const raw = (mimeType || '').trim().toLowerCase();
  if (raw && raw !== 'application/octet-stream' && raw !== 'binary/octet-stream') {
    return raw;
  }
  const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
  const byExt: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
  };
  return byExt[ext] || raw || 'application/octet-stream';
}

function isPdfPreview(mime: string, fileName?: string | null) {
  const ext = (fileName?.split('.').pop() || '').toLowerCase();
  return mime.includes('pdf') || ext === 'pdf';
}

function isImagePreview(mime: string, fileName?: string | null) {
  const ext = (fileName?.split('.').pop() || '').toLowerCase();
  return mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff'].includes(ext);
}

function messageFromPayload(payload: any): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload.msg || payload.error || payload.message;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

async function throwIfJsonErrorBlob(blob: Blob) {
  const peek = (await blob.slice(0, 120).text()).trim();
  if (!peek.startsWith('{') && !peek.startsWith('[')) return;
  const parsed = JSON.parse(await blob.text());
  throw new Error(messageFromPayload(parsed) || 'Failed to load document');
}

interface TxDocumentPreviewDialogProps {
  open: boolean;
  document: TxPreviewDocument;
  onClose: () => void;
}

export const TxDocumentPreviewDialog: React.FC<TxDocumentPreviewDialogProps> = ({
  open,
  document: doc,
  onClose,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState('application/octet-stream');

  useEffect(() => {
    const openUrl = doc?.open_url;
    if (!open || !openUrl) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewUrl(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('You are not signed in. Refresh the page and log in again.');
        }
        const response = await api.get(openUrl, {
          responseType: 'blob',
          timeout: 180000,
          headers: { Authorization: `Bearer ${token}` },
        });
        const blobData: Blob = response.data;
        await throwIfJsonErrorBlob(blobData);
        const mime = guessPreviewMime(blobData.type, doc.file_name);
        objectUrl = URL.createObjectURL(
          blobData.type === mime ? blobData : new Blob([blobData], { type: mime })
        );
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setPreviewMime(mime);
        setPreviewUrl(objectUrl);
      } catch (err: any) {
        let message = 'Failed to load document';
        const data = err.response?.data;
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text());
            message = messageFromPayload(parsed) || err.message || message;
          } catch {
            message = err.message || message;
          }
        } else if (err.response?.data) {
          message = messageFromPayload(err.response.data) || err.message || message;
        } else {
          message = err.message || message;
        }
        if (message === 'Missing Authorization Header') {
          message =
            'Could not load the document because the request was not signed in. Refresh the page and try again.';
        }
        if (!cancelled) {
          setPreviewError(message);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };
    void loadPreview();
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, doc?.open_url, doc?.file_name]);

  const titleKind = TITLE_BY_TYPE[doc?.document_type || ''] || 'Document';
  const title = doc?.file_name ? `${titleKind} — ${doc.file_name}` : titleKind;

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = window.document.createElement('a');
    a.href = previewUrl;
    a.download = doc?.file_name || 'document';
    a.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        sx={{
          height: '75vh',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {previewLoading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {!previewLoading && previewError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {previewError}
          </Alert>
        )}
        {!previewLoading && !previewError && previewUrl && (
          isPdfPreview(previewMime, doc?.file_name) ? (
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <iframe
                src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Document preview"
              />
            </Box>
          ) : isImagePreview(previewMime, doc?.file_name) ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt={doc?.file_name || 'Document'}
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 2 }}
              />
            </Box>
          ) : (
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ flex: 1, p: 4 }}>
              <Typography color="text.secondary">
                Preview is not available for this file type. Download it to open locally.
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                Download
              </Button>
            </Stack>
          )
        )}
      </DialogContent>
      <DialogActions>
        {previewUrl ? (
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
          >
            Open in new tab
          </Button>
        ) : null}
        {previewUrl ? (
          <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TxDocumentPreviewDialog;
